import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Check, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminFetchComments, adminApproveComment, adminDeleteComment } from "@/lib/auth";

export const AdminComments = () => {
    const [comments, setComments] = useState([]);
    const [busy, setBusy] = useState(null);

    const load = () => adminFetchComments().then(setComments).catch(() => {});
    useEffect(() => { load(); }, []);

    const pending = comments.filter((c) => !c.approved);
    const approved = comments.filter((c) => c.approved);

    const approve = async (c) => {
        setBusy(c.id);
        try {
            await adminApproveComment(c.id);
            toast.success("Comentário aprovado e publicado.");
            load();
        } catch {
            toast.error("Não foi possível aprovar.");
        } finally {
            setBusy(null);
        }
    };

    const remove = async (c) => {
        if (!window.confirm("Excluir este comentário definitivamente?")) return;
        setBusy(c.id);
        try {
            await adminDeleteComment(c.id);
            toast.success("Comentário excluído.");
            load();
        } catch {
            toast.error("Não foi possível excluir.");
        } finally {
            setBusy(null);
        }
    };

    const Row = ({ c, isPending }) => (
        <div data-testid={`admin-comment-${c.id}`} className="border border-white/10 bg-paper p-5 flex items-start gap-4">
            <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-3 mb-1.5">
                    <span className="font-medium text-sm">{c.name}</span>
                    <Link to={`/artigo/${c.article_slug}`} target="_blank" className="text-xs text-gold hover:text-golddark transition-colors">
                        /artigo/{c.article_slug}
                    </Link>
                    <span className="text-[11px] text-slate-600 ml-auto">{new Date(c.created_at).toLocaleString("pt-BR")}</span>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed">{c.text}</p>
            </div>
            <div className="flex gap-2 shrink-0">
                {isPending && (
                    <button data-testid={`comment-approve-${c.id}`} onClick={() => approve(c)} disabled={busy === c.id} className="p-2 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500 hover:text-ink transition-colors disabled:opacity-50" title="Aprovar">
                        {busy === c.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    </button>
                )}
                <button data-testid={`comment-delete-${c.id}`} onClick={() => remove(c)} disabled={busy === c.id} className="p-2 border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-400/50 transition-colors disabled:opacity-50" title="Excluir">
                    <Trash2 size={14} />
                </button>
            </div>
        </div>
    );

    return (
        <div className="mt-14">
            <h2 className="font-serif text-2xl tracking-tight mb-6 flex items-center gap-3">
                <MessageSquare size={18} className="text-gold" /> Comentários
                {pending.length > 0 && (
                    <span data-testid="comments-pending-badge" className="text-xs font-sans bg-gold text-ink px-2.5 py-1 font-medium">{pending.length} aguardando</span>
                )}
            </h2>

            {comments.length === 0 ? (
                <p className="text-sm text-slate-500 border border-dashed border-white/15 px-5 py-8 text-center">Nenhum comentário recebido ainda.</p>
            ) : (
                <div className="flex flex-col gap-3">
                    {pending.map((c) => <Row key={c.id} c={c} isPending />)}
                    {approved.length > 0 && pending.length > 0 && (
                        <p className="text-[10px] uppercase tracking-[0.25em] text-slate-600 mt-4">Aprovados</p>
                    )}
                    {approved.map((c) => <Row key={c.id} c={c} isPending={false} />)}
                </div>
            )}
        </div>
    );
};
